/**
 * Data type for Dates.
 */
export class DatePart {

	/**
	 * The year part of the date
	 */
	public Year: number;

	/**
	 * The month part of the date
	 * Note: Month is 1 indexed (January is 1, December is 12) to align with common date formatting and to avoid confusion with JavaScript's Date object which is 0 indexed for months.
	 */
	public Month: number;

	/**
	 * The day part of the date
	 */
	public Day: number;

	/**
	 * @constructor
	 * @param date The date to use to build the date.
	 */
	constructor(date: string | Date) {
		if (date instanceof Date) {
			this.Year = date.getFullYear();
			// getMonth is zero-based, so we add 1 to align with DatePart's 1-indexed month
			this.Month = date.getMonth() + 1;
			this.Day = date.getDate();
			return;
		}

		const dateRegex: RegExp = /^(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

		// Ensure that the provided string matches the expected date mask
		if (!dateRegex.test(date)) {
			let convertToDate: Date = Date.parse(date) ? new Date(date) : new Date(NaN);
			if (dateRegex.test(convertToDate.toISOString().split('T')[0])) {
				date = convertToDate.toISOString().split('T')[0];
			}
			else {
				this.LogError('Invalid date format. Please use "yyyy-MM-dd".');
			}
		}

		const dateParts: string[] = date.split('-');

		// Parse year, month, and day from the string parts
		const year: number = parseInt(dateParts[0]);
		const month: number = parseInt(dateParts[1]);
		const day: number = parseInt(dateParts[2]);

		// Validate year, month, and day
		if (isNaN(year) || isNaN(month) || isNaN(day)) {
			this.LogError('Invalid date format. Please use valid numbers for year, month, and day.');
		}

		// Validate month range (1 to 12)
		if (month < 1 || month > 12) {
			this.LogError('Invalid month. Month should be between 1 and 12.');
		}

		// Validate day range based on the month
		// Get the last day of the month
		const daysInMonth: number = new Date(year, month, 0).getDate();
		if (day < 1 || day > daysInMonth) {
			this.LogError(`Invalid day. Day should be between 1 and ${daysInMonth}.`);
		}

		// If all validations pass, assign the values to the fields
		this.Year = year;
		this.Month = month;
		this.Day = day;
	}

	/**
	 * Overrides the toString method on DatePart for serialization.
	 * @returns The date formatted as 'yyyy-MM-dd'
	 */
	public toString(): string {
		const zeroPad: (num: number) => string = (num: number) => String(num).padStart(2, '0');
		return `${this.Year}-${zeroPad(this.Month)}-${zeroPad(this.Day)}`;
	}

	/**
	 * Creates a Date object from a DatePart, setting the time to 00:00:00.000.
	 * @returns The Date object representing the given DatePart with time set to the start of the day.
	 */
	public toDate(): Date {
		// Subtracting 1 from month because DatePart month is 1 indexed and JS Date month is 0 indexed.
		return new Date(this.Year, this.Month - 1, this.Day, 0, 0, 0, 0);
	}

	/**
	 * Used as a substitute for getDate on a Date object to return the day for compatibility with JavaScript Date objects.
	 * @returns The day as a number between 1 and 31 depending on the month and year.
	 */
	public getDate(): number {
		return this.Day;
	}

	/**
	 * Used as a substitute for getMonth on a Date object to return a 0 indexed month for compatibility with JavaScript Date objects.
	 * @returns The month as a number between 0 (January) and 11 (December).
	 */
	public getMonth(): number {
		// Subtracting 1 from month because DatePart month is 1 indexed and JS Date month is 0 indexed.
		return this.Month - 1;
	}

	/**
	 * Used as a substitute for getFullYear on a Date object to return the year for compatibility with JavaScript Date objects.
	 * @returns The year as a number.
	 */
	public getFullYear(): number {
		return this.Year;
	}

	/**
	 * Logs an error message.
	 * @param message The message to log.
	 */
	private LogError(message: string): void {
		console.error(message);
		throw new Error(message);
	}

}